import { useMemo, useState } from 'react';
import { SearchableSelect } from '@shared/components';
import { useDealersQuery } from '../hooks/useDealersQuery';
import type { Dealer } from '../types/dealers.types';

interface DealerSelectProps {
  id?: string;
  onSelect: (dealer: Dealer | null) => void;
  selected?: Dealer | null;
  placeholder?: string;
  excludeIds?: string[];
  filterIsGroup?: boolean;
  minChars?: number;
  perPage?: number;
}

export function DealerSelect({
  id,
  onSelect,
  selected,
  placeholder = 'Search dealers by name, code, city, or postcode...',
  excludeIds = [],
  filterIsGroup,
  minChars = 2,
  perPage = 20,
}: DealerSelectProps) {
  // SearchableSelect owns the debounce and emits only settled terms; the
  // minChars gate is preserved by only enabling the query (and only supplying
  // options) once the settled term is long enough.
  const [term, setTerm] = useState('');
  const canQuery = term.length >= minChars;

  const params = useMemo(
    () => ({
      ...(canQuery ? { search: term } : {}),
      per_page: perPage,
      ...(typeof filterIsGroup === 'boolean' ? { is_group: filterIsGroup } : {}),
    }),
    [canQuery, term, perPage, filterIsGroup],
  );

  const { data, isFetching } = useDealersQuery(params, { enabled: canQuery });

  return (
    <SearchableSelect<Dealer>
      id={id}
      options={canQuery ? (data?.data ?? []) : []}
      value={selected ?? null}
      onChange={onSelect}
      getOptionValue={(d) => d.id}
      getOptionLabel={(d) => d.name}
      onSearchChange={setTerm}
      loading={isFetching}
      excludeValues={excludeIds}
      clearable
      placeholder={placeholder}
      emptyLabel={canQuery ? 'No dealers match.' : `Type at least ${minChars} characters to search.`}
      renderOption={(d) => (
        <div className="min-w-0 text-left w-full">
          <div className="font-semibold text-sm">{d.name}</div>
          <div className="text-xs text-mp-muted flex gap-2">
            <span className="font-mono">{d.dealer_code}</span>
            {d.city && <span>· {d.city}</span>}
            {d.postcode && <span>· {d.postcode}</span>}
          </div>
        </div>
      )}
    />
  );
}
